import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type {
  OccupancyReport,
  OccupancyStatsReport,
  RevenueReport,
  ExpenseSummaryReport,
  ExpenseTrendReport,
  ProfitLossReport,
  ProfitLossBasis,
  CollectionStatsReport,
  TenantScorecardReport,
  TenantScorecardDetail,
  OverdueInvoicesReport,
  VacantUnitsReport,
  TimesheetsReport,
  MaintenanceReport,
  CafCollectionReport,
  BankReconciliationReport,
  FinancialStatementsReport,
} from '@/types'

export function useOccupancyReport(propertyId?: number, from?: string, to?: string) {
  return useQuery({
    queryKey: ['reports', 'occupancy', { propertyId, from, to }],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (propertyId) params.property_id = propertyId
      if (from) params.from = from
      if (to) params.to = to
      const { data } = await api.get<{ data: OccupancyReport }>('/api/reports/occupancy', { params })
      return data.data
    },
  })
}

// RPT-6: Occupancy Stats hook — extends useOccupancyReport with granularity + group params
export function useOccupancyStatsReport(params: {
  propertyId?: number
  from?: string
  to?: string
  granularity?: string
}) {
  return useQuery({
    queryKey: ['reports', 'occupancy_stats', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      if (params.granularity) queryParams.granularity = params.granularity
      const { data } = await api.get<{ data: OccupancyStatsReport }>('/api/reports/occupancy', {
        params: queryParams,
      })
      return data.data
    },
  })
}

// RPT-6: Vacant Units hook (2b)
export function useVacantUnitsReport(params: {
  propertyId?: number
  unitType?: string
  vacantSince?: string
}) {
  return useQuery({
    queryKey: ['reports', 'vacant_units', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.unitType) queryParams.unit_type = params.unitType
      if (params.vacantSince) queryParams.vacant_since = params.vacantSince
      const { data } = await api.get<{ data: VacantUnitsReport }>('/api/reports/vacant_units', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useRevenueReport(propertyId?: number, period?: string, from?: string, to?: string) {
  return useQuery({
    queryKey: ['reports', 'revenue', { propertyId, period, from, to }],
    queryFn: async () => {
      const params: Record<string, unknown> = {}
      if (propertyId) params.property_id = propertyId
      if (period) params.period = period
      if (from) params.from = from
      if (to) params.to = to
      const { data } = await api.get<{ data: RevenueReport }>('/api/reports/revenue', { params })
      return data.data
    },
  })
}

export function useExpenseSummaryReport(params: {
  propertyId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'expense_summary', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: ExpenseSummaryReport }>('/api/reports/expense_summary', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useExpenseTrendReport(params: {
  propertyId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'expense_trend', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: ExpenseTrendReport[] }>('/api/reports/expense_trend', {
        params: queryParams,
      })
      return data.data
    },
  })
}

// RPT-7: Extended with granularity + group (property ids for per-property trend lines)
export function useProfitLossReport(params: {
  basis?: ProfitLossBasis
  propertyId?: number
  from?: string
  to?: string
  granularity?: string
  group?: number[]
}) {
  return useQuery({
    queryKey: ['reports', 'profit_loss', { ...params, basis: params.basis ?? 'billed' }],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = { basis: params.basis ?? 'billed' }
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      if (params.granularity) queryParams.granularity = params.granularity
      if (params.group && params.group.length > 0) queryParams.group = params.group.join(',')
      const { data } = await api.get<{ data: ProfitLossReport }>('/api/reports/profit_loss', {
        params: queryParams,
      })
      return data.data
    },
  })
}

// RPT-5: Collections report hooks

export function useCollectionStatsReport(params: {
  propertyId?: number
  unitId?: number
  from?: string
  to?: string
  granularity?: string
}) {
  return useQuery({
    queryKey: ['reports', 'collection_stats', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.unitId) queryParams.unit_id = params.unitId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      if (params.granularity) queryParams.granularity = params.granularity
      const { data } = await api.get<{ data: CollectionStatsReport }>('/api/reports/collection_stats', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useTenantScorecardReport(params: {
  lookbackMonths?: number
}) {
  return useQuery({
    queryKey: ['reports', 'tenant_scorecard', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.lookbackMonths) queryParams.lookback_months = params.lookbackMonths
      const { data } = await api.get<{ data: TenantScorecardReport }>('/api/reports/tenant_scorecard', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useTenantScorecardDetail(tenantId: number | undefined) {
  return useQuery({
    queryKey: ['reports', 'tenant_scorecard_detail', tenantId],
    queryFn: async () => {
      const { data } = await api.get<{ data: TenantScorecardDetail }>('/api/reports/tenant_scorecard', {
        params: { tenant_id: tenantId },
      })
      return data.data
    },
    enabled: tenantId !== undefined && tenantId > 0,
  })
}

export function useOverdueInvoicesReport(params: {
  propertyId?: number
  unitId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'overdue_invoices', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.unitId) queryParams.unit_id = params.unitId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: OverdueInvoicesReport }>('/api/reports/overdue_invoices', {
        params: queryParams,
      })
      return data.data
    },
  })
}

// ===== RPT-8 — Juristic / Management Records reports (4a-4e) =====

export function useTimesheetsReport(params: {
  propertyId?: number
  userId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'timesheets', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.userId) queryParams.user_id = params.userId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: TimesheetsReport }>('/api/reports/timesheets', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useMaintenanceReport(params: {
  propertyId?: number
  unitId?: number
  status?: string
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'maintenance', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.unitId) queryParams.unit_id = params.unitId
      if (params.status) queryParams.status = params.status
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: MaintenanceReport }>('/api/reports/maintenance', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useCafCollectionReport(params: {
  propertyId?: number
  unitId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'caf_collection', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.unitId) queryParams.unit_id = params.unitId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: CafCollectionReport }>('/api/reports/caf_collection', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useBankReconciliationReport(params: {
  bankName?: string
  statementId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'bank_reconciliation', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.bankName) queryParams.bank_name = params.bankName
      if (params.statementId) queryParams.statement_id = params.statementId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: BankReconciliationReport }>('/api/reports/bank_reconciliation', {
        params: queryParams,
      })
      return data.data
    },
  })
}

export function useFinancialStatementsReport(params: {
  propertyId?: number
  from?: string
  to?: string
}) {
  return useQuery({
    queryKey: ['reports', 'financial_statements', params],
    queryFn: async () => {
      const queryParams: Record<string, unknown> = {}
      if (params.propertyId) queryParams.property_id = params.propertyId
      if (params.from) queryParams.from = params.from
      if (params.to) queryParams.to = params.to
      const { data } = await api.get<{ data: FinancialStatementsReport }>('/api/reports/financial_statements', {
        params: queryParams,
      })
      return data.data
    },
  })
}
